/**
 * GuestSharePage — public landing page for guest share links.
 *
 * Route: /share/:shareId
 *
 * Uses the "db swap" pattern: before rendering, swaps the global `db`
 * binding to `guestDb` so ALL app components (TaskItem, TaskDetail, etc.)
 * automatically query the guest Dexie instance. Safe because this route
 * renders outside the main app shell (no AppLayout/DatabaseProvider).
 *
 * @see docs/plans/permissions-plan.md §8
 */

import { useCallback, useEffect, useRef, useState, useMemo } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useLiveQueryImmediate } from "@/db/hooks"
import { CircularProgress } from "@/app/components/ui/CircularProgress"
import { ObjectStatus, ObjectType, swapDb, restoreDb } from "@/db/schema"
import type { DbStartAtIndex } from "@/db/schema"
import { getGuestDb } from "@/db/guestDb"
import { setGuestMode } from "@totodo/operations"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { useViewContext } from "@/app/stores/viewContext"
import { initializeAnalytics, logAnalyticsEvent } from "@/shared/services/analytics"
import type { ViewItem } from "@/features/views/hooks/useViewData"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PageState =
  | { status: "loading"; message?: string }
  | { status: "error"; message: string }
  | { status: "synced"; containerId: string }

// ---------------------------------------------------------------------------
// No-op handlers — guest view is read-only (writes will fail silently)
// ---------------------------------------------------------------------------

const noopId = (_id: string) => {}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GuestSharePage() {
  const { shareId } = useParams<{ shareId: string }>()
  const { t } = useTranslation()
  const [state, setState] = useState<PageState>({ status: "loading" })
  const analyticsInitRef = useRef(false)
  const loginRef = useRef(false)
  const dbSwappedRef = useRef(false)

  // Initialize Firebase Analytics once (starts in cookieless/denied mode)
  useEffect(() => {
    if (analyticsInitRef.current) return
    analyticsInitRef.current = true
    void initializeAnalytics()
  }, [])

  // Login as demo user via Dexie Cloud → triggers sync
  useEffect(() => {
    if (!shareId || !/^[A-Za-z0-9]{8,20}$/.test(shareId)) {
      setState({ status: "error", message: "invalid_link" })
      return
    }
    if (loginRef.current) return
    loginRef.current = true

    const demoUserId = `share-${shareId}@demo.local`
    let cancelled = false

    setState({ status: "loading", message: "Connecting…" })

    ;(async () => {
      try {
        const guestDb = getGuestDb()

        if (!guestDb.isOpen()) {
          await guestDb.open()
        }
        if (cancelled) return

        await guestDb.cloud.login({ grant_type: "demo", userId: demoUserId })
        if (cancelled) return

        await guestDb.cloud.sync()
        if (cancelled) return

        // Accept the invite with a proper Date object
        const memberId = `mmb-guest-${shareId}`
        const member = await guestDb.table("members").get(memberId)
        if (member && !(member.accepted instanceof Date)) {
          await guestDb.table("members").update(memberId, {
            accepted: new Date(),
          })
        }
        if (cancelled) return

        setState({ status: "loading", message: "Syncing…" })

        const containerId = await waitForContainer(guestDb, 30_000)
        if (cancelled) return

        if (!containerId) {
          setState({ status: "error", message: "not_found" })
          return
        }

        // Swap global db → guestDb so all app components query guest data
        swapDb(guestDb)
        setGuestMode(true)
        dbSwappedRef.current = true

        // Configure viewContext for "list" view so TaskItem reads correct state
        useViewContext.getState().setView("list", containerId)

        setState({ status: "synced", containerId })

        void logAnalyticsEvent("guest_share_viewed", { share_id: shareId })
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : "unknown_error"
        console.error("[GuestSharePage] Login/sync failed:", msg)
        setState({ status: "error", message: msg })
        void logAnalyticsEvent("guest_share_error", {
          share_id: shareId,
          error: msg,
        })
      }
    })()

    return () => {
      cancelled = true
      loginRef.current = false
      if (dbSwappedRef.current) {
        setGuestMode(false)
        restoreDb()
        dbSwappedRef.current = false
      }
    }
  }, [shareId])

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="flex items-center gap-2 border-b border-foreground/5 px-6 py-4">
        <img
          src="/favicons/favicon-96x96.png"
          alt="ToToDo"
          className="size-6"
        />
        <span className="text-sm font-semibold text-foreground/80">
          ToToDo
        </span>
      </header>

      {/* Content */}
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col px-6 py-8">
        {state.status === "loading" && <LoadingView message={state.message} />}
        {state.status === "error" && (
          <ErrorView message={state.message} t={t} />
        )}
        {state.status === "synced" && (
          <SyncedView containerId={state.containerId} t={t} />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-foreground/5 px-6 py-4 text-center">
        <span className="text-xs text-foreground/30">
          {t("Shared via")}{" "}
          <a
            href="https://totodo.app"
            className="text-foreground/50 hover:text-foreground/70"
            target="_blank"
            rel="noopener noreferrer"
          >
            totodo.app
          </a>
        </span>
      </footer>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function waitForContainer(
  guestDb: ReturnType<typeof getGuestDb>,
  maxWaitMs = 15_000,
): Promise<string | null> {
  const start = Date.now()
  while (Date.now() - start < maxWaitMs) {
    const all = await guestDb.totodo.toArray()
    const container = all.find(
      (t) =>
        (t.type === ObjectType.Project || t.type === ObjectType.List) &&
        !t.isDeleted,
    )
    if (container?.id) return container.id
    await new Promise((r) => setTimeout(r, 300))
  }
  return null
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function LoadingView({ message }: { message?: string }) {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <CircularProgress className="size-6" />
        <span className="text-sm text-foreground/40">
          {message || "Loading…"}
        </span>
      </div>
    </div>
  )
}

function ErrorView({
  message,
  t,
}: {
  message: string
  t: (key: string) => string
}) {
  const isNotFound = message === "not_found" || message === "invalid_link"

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
      <div className="rounded-full bg-foreground/5 p-4">
        <span className="text-2xl">{isNotFound ? "🔗" : "⚠️"}</span>
      </div>
      <h1 className="text-lg font-semibold text-foreground/80">
        {isNotFound
          ? t("This share link is no longer valid")
          : t("Something went wrong")}
      </h1>
      <p className="max-w-sm text-sm text-foreground/50">
        {isNotFound
          ? t("The link may have expired or been revoked by the owner.")
          : t("Could not load the shared content. Please try again later.")}
      </p>
      <a
        href="https://totodo.app"
        className="mt-2 rounded-md bg-foreground/5 px-4 py-2 text-sm text-foreground/60 transition-colors hover:bg-foreground/10"
      >
        {t("Go to ToToDo")}
      </a>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SyncedView — uses REAL TaskItem components from the app.
//
// After db swap, `db` points to guestDb. All app components that import `db`
// from "@/db/schema" now query guestDb via ES module live bindings.
// ---------------------------------------------------------------------------

function SyncedView({
  containerId,
  t,
}: {
  containerId: string
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTaskId = searchParams.get("tid")
  const guestDb = getGuestDb()

  // Live query: container object
  const [container] = useLiveQueryImmediate(
    () => guestDb.totodo.get(containerId),
    ["guest-container", containerId],
    undefined,
  )

  // Live query: tasks + sections in this container
  const parentPath = `root/${containerId}`
  const [tasks] = useLiveQueryImmediate(
    () =>
      guestDb.totodo
        .where("[parentPath+isDeleted]")
        .equals([parentPath, 0])
        .filter(
          (t) =>
            (t.type === ObjectType.Task || t.type === ObjectType.Section) &&
            t.status === ObjectStatus.Unfinished,
        )
        .toArray(),
    ["guest-tasks", parentPath],
    undefined,
  )

  // Live query: startAtIndex for sorting
  const startAtIds = useMemo(
    () => (tasks ?? []).filter((t) => !!t.id).map((t) => `#${t.id!}`),
    [tasks],
  )
  const [startAtEntries] = useLiveQueryImmediate(
    () =>
      startAtIds.length > 0
        ? guestDb.startAtIndex.bulkGet(startAtIds)
        : [],
    ["guest-startAt", startAtIds],
    undefined,
  )

  // Build ViewItem[] — same shape that ViewShell feeds to TaskItem
  const viewItems = useMemo(() => {
    if (!tasks || !startAtEntries) return []
    const entryMap = new Map<string, DbStartAtIndex>()
    for (let i = 0; i < startAtIds.length; i++) {
      const sid = startAtIds[i]
      const raw = startAtEntries[i]
      if (sid && raw) entryMap.set(sid.slice(1), raw)
    }
    return [...tasks]
      .filter((t) => !!t.id)
      .map((t) => {
        const entry = entryMap.get(t.id!)
        return {
          task: t,
          sortIndex: entry?.index ?? t.index,
          startAtData: entry,
        } satisfies ViewItem
      })
      .sort((a, b) => a.sortIndex - b.sortIndex)
  }, [tasks, startAtEntries, startAtIds])

  // Open/close task detail via URL params (?tid=)
  const openTask = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set("tid", id)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const closeTask = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("tid")
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  // Push visible task IDs to viewContext (needed by TaskItem for selection)
  const visibleIds = useMemo(
    () => viewItems.map((v) => v.task.id!),
    [viewItems],
  )
  useEffect(() => {
    useViewContext.getState().setVisibleTaskIds(visibleIds)
    useViewContext.getState().setVisibleSectionIds(
      new Set(
        viewItems
          .filter((v) => v.task.type === ObjectType.Section)
          .map((v) => v.task.id!),
      ),
    )
  }, [visibleIds, viewItems])

  // Click-outside-to-close — mirrors ViewShell's pointerdown capture handler
  useEffect(() => {
    if (!activeTaskId) return

    let touchStartX = 0
    let touchStartY = 0
    let touchMoved = false
    let touchPending = false
    const TAP_THRESHOLD = 10

    function isOutsideActiveTask(target: Element): boolean {
      const activeCard = document.querySelector(".task-row-active")
      if (activeCard?.contains(target)) return false
      if (
        target.closest(
          "[data-action-panel], #editor-toolbar-slot, [data-floating-button], [role='menu']",
        )
      )
        return false
      if (
        document.querySelector(
          '[role="dialog"], [data-vaul-drawer], [data-radix-portal]',
        )
      )
        return false
      return true
    }

    const downHandler = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (!target || !isOutsideActiveTask(target)) return
      if (e.pointerType === "mouse" || e.pointerType === "pen") {
        closeTask()
        return
      }
      touchStartX = e.clientX
      touchStartY = e.clientY
      touchMoved = false
      touchPending = true
    }

    const moveHandler = (e: PointerEvent) => {
      if (!touchPending || e.pointerType === "mouse") return
      const dx = Math.abs(e.clientX - touchStartX)
      const dy = Math.abs(e.clientY - touchStartY)
      if (dx > TAP_THRESHOLD || dy > TAP_THRESHOLD) touchMoved = true
    }

    const upHandler = (e: PointerEvent) => {
      if (!touchPending || e.pointerType === "mouse") return
      if (!touchMoved) closeTask()
      touchPending = false
    }

    const cancelHandler = () => {
      touchPending = false
    }

    document.addEventListener("pointerdown", downHandler, true)
    document.addEventListener("pointermove", moveHandler, true)
    document.addEventListener("pointerup", upHandler, true)
    document.addEventListener("pointercancel", cancelHandler, true)
    return () => {
      document.removeEventListener("pointerdown", downHandler, true)
      document.removeEventListener("pointermove", moveHandler, true)
      document.removeEventListener("pointerup", upHandler, true)
      document.removeEventListener("pointercancel", cancelHandler, true)
    }
  }, [activeTaskId, closeTask])

  if (!container || !tasks) return <LoadingView />

  const taskCount = viewItems.filter(
    (v) => v.task.type === ObjectType.Task,
  ).length

  return (
    <div className="flex flex-col gap-6">
      {/* Container heading */}
      <div className="px-3 pt-2">
        <h1 className="text-xl font-bold text-foreground/90">
          {container.title}
        </h1>
        <p className="mt-0.5 text-[11px] text-foreground/40">
          {taskCount} {taskCount === 1 ? t("task") : t("tasks")}
        </p>
      </div>

      {/* Task list — REAL TaskItem components with dimming + animation */}
      {viewItems.length > 0 ? (
        <div className={activeTaskId ? "task-list-dimmed" : undefined}>
          {viewItems.map((item, idx) => (
            <TaskItem
              key={item.task.id}
              task={item.task}
              startAtData={item.startAtData}
              isActive={activeTaskId === item.task.id}
              onOpen={openTask}
              onClose={closeTask}
              onComplete={noopId}
              onUncomplete={noopId}
              isFirstInGroup={
                idx === 0 ||
                viewItems[idx - 1]?.task.type === ObjectType.Section
              }
              isLastInGroup={
                idx === viewItems.length - 1 ||
                viewItems[idx + 1]?.task.type === ObjectType.Section
              }
            />
          ))}
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-foreground/30">
          {t("No tasks yet")}
        </p>
      )}

      {/* CTA */}
      <div className="mt-4 flex flex-col items-center gap-3 rounded-lg border border-foreground/5 bg-foreground/[0.02] p-6 text-center">
        <p className="text-sm text-foreground/50">
          {t("Want your own task lists?")}
        </p>
        <a
          href="https://totodo.app"
          className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90"
          onClick={() => {
            void logAnalyticsEvent("guest_share_cta_click", {
              container_type:
                container.type === ObjectType.Project ? "project" : "list",
              task_count: taskCount,
            })
          }}
        >
          {t("Try ToToDo for free")}
        </a>
      </div>
    </div>
  )
}
